import { Link } from "react-router-dom";
import ApeCarousel from "../components/ApeCarousel";
import EarningsSimulator from "../components/EarningsSimulator";
import NftRightsStack from "../components/NftRightsStack";
import TrustBar from "../components/TrustBar";
import TokenomicsDiagram from "../components/TokenomicsDiagram";
import { getCodeApeXUrl, getDisplayCapeTokenAddress, getDisplayNftAddress, getExplorerAddressUrl, getUniswapTradeUrl, shortAddress } from "../utils/launchLinks";
import { MINT_PRICE_ETH, TOTAL_SUPPLY_CAP, envString } from "../utils/config";

const SITE_URL = envString("VITE_CODE_APE_SITE_URL") ?? "https://code-ape.com";
const SKILL_URL = envString("VITE_CODE_APE_SKILL_URL") ?? "https://code-ape.com/skill";
const CANONICAL_SKILL_URL = `${SITE_URL.replace(/\/+$/, "")}/skill/RUN_INVENTORY.md`;
const CODEX_PROMPT = `Codex, read ${CANONICAL_SKILL_URL} and prepare my CODE APE mint. Do not generate images; request the inventory handoff and return the mintUrl first.`;

const STATS = [
  { value: TOTAL_SUPPLY_CAP.toLocaleString(), label: "AI-agent Apes" },
  { value: `${MINT_PRICE_ETH} ETH`, label: "Mint price" },
  { value: "1% WETH", label: "Hook fee stream" },
  { value: "5,000+", label: "CAPE if you burn" },
  { value: "90 days", label: "Community LP lock" },
  { value: "10%", label: "Invite reward" }
];

const FIRSTS = [
  {
    index: "01",
    pillar: "ORIGIN",
    title: "Creation becomes part of the mint",
    body: "Most NFT provenance starts after the art exists. CODE APE moves the creation path into the mint flow: public Skill, reference pack, image hash, metadata hash, wallet, chain, contract, and protocol-signed voucher all have to line up before the wallet transaction can succeed.",
    caveat: "AI-signed means the protocol signer validates the AI-agent handoff; the AI does not hold a private key."
  },
  {
    index: "02",
    pillar: "AGENT",
    title: "Codex first, AI-agent compatible",
    body: "Codex is the recommended client because it gives the smoothest summon flow today. The protocol itself does not check a vendor logo. It checks whether the public Skill and authenticated handoff produce the exact fields the contract expects.",
    caveat: "Claude Code, Cursor, Cline, and custom agents can be compatible if they can run the Skill and call the backend flow."
  },
  {
    index: "03",
    pillar: "HOOK",
    title: "NFT holders share trading fees",
    body: "CODE APE treats the NFT as the bearer receipt for official pool trading fees. Active NFTs share WETH from the hook together. Transfer the NFT and the entitlement moves. Burn it and the future fee stream stops.",
    caveat: "Fees depend on real official-pool swaps and are not guaranteed yield."
  },
  {
    index: "04",
    pillar: "LP",
    title: "Launch liquidity becomes NFT-owned",
    body: "At finalize, the official v4 LP locks in UNCX before trading opens. After the 90-day unlock, the community vault lets current unburned NFT holders claim split v4 LP positions, so the owner directly holds that liquidity.",
    caveat: "Burning before LP claim forfeits that LP right and concentrates the remaining LP among unclaimed active NFTs."
  }
] as const;

const HOLD_ITEMS = [
  "Every official swap sends WETH to active NFT holders as a group.",
  "Both directions count: WETH to CAPE and CAPE to WETH.",
  "Claim any time, or let pending fees accumulate.",
  "Simple game: hold to keep fees and LP optionality."
];

const BURN_ITEMS = [
  "Burn your Ape any time to claim 5,000 CAPE base.",
  "At the initial LP ratio, 5,000 CAPE maps to 0.005 ETH before market movement.",
  "Dynamic bonus can add more CAPE while the bonus pool lasts.",
  "Burning settles pending WETH first, then stops future NFT fees.",
  "Final decision: once burned, the NFT and unclaimed LP right cannot come back."
];

const HOW_IT_WORKS = [
  ["1", "Authorize the agent", "Sign one login message so Codex or a compatible agent can create a mint session for your wallet. No ETH moves."],
  ["2", "Recommended: Codex", "Codex runs the compact Skill, requests a pre-pinned inventory handoff, and receives the signed mint pack."],
  ["3", "Open the mint link", "The agent returns a short link like code-ape.com/mint?h=CA-XXXX. Open it to review your Ape."],
  ["4", "Choose wallet and mint", "Use All wallets, WalletConnect, or your browser wallet, then approve the final mint transaction."]
];

const FAQ = [
  ["Is CODE APE an OpenAI project?", "No. Codex is the recommended client, not a required vendor or partnership claim. The protocol checks the public Skill, wallet-bound handoff, hashes, chain, contract, and voucher."],
  ["Why start with Codex?", "Codex is the smoothest recommended client today. CODE APE stays AI-agent compatible: Claude Code, Cursor, Cline, and custom agents can work when they can run the Skill and call the authenticated backend flow."],
  ["Why not generate directly on the website?", "Speed, consistency, provenance, and anti-spam. The artwork is pre-pinned into a limited inventory, while the mint handoff still requires a wallet-authorized AI-agent step."],
  ["What does holding do?", "Active NFTs share the WETH trading-fee stream from the official pool. The simple game is hold to keep future fees and LP optionality, or burn later to take CAPE."],
  ["What does burning do?", "Burning settles pending WETH first, then claims 5,000 CAPE base plus capped dynamic bonus from the claim vault. At the initial LP ratio, the 5,000 CAPE base maps to 0.005 ETH before market movement. It is final and the burned NFT no longer earns future WETH."],
  ["Is there an admin rescue button?", "No. The protocol is designed without admin pause, upgrade, LP withdraw, or hidden buyback controls. Read the proof page before minting."],
  ["Why no staking?", "Because the NFT is already the receipt for WETH fees. There is no separate fee vault or staking deposit. The Uniswap v4 hook routes the official fee stream to active NFT holders. Separately, the launch LP is locked in UNCX for 90 days and becomes claimable through the community vault after unlock."]
];

export default function LandingPage() {
  const nftAddress = getDisplayNftAddress();
  const capeTokenAddress = getDisplayCapeTokenAddress();
  const nftExplorerUrl = nftAddress ? getExplorerAddressUrl(nftAddress) : null;
  const capeTokenExplorerUrl = capeTokenAddress ? getExplorerAddressUrl(capeTokenAddress) : null;
  const xUrl = getCodeApeXUrl();
  const uniswapTradeUrl = getUniswapTradeUrl();

  async function copyPrompt() {
    await navigator.clipboard?.writeText(CODEX_PROMPT);
  }

  async function copyNftAddress() {
    if (!nftAddress) return;
    await navigator.clipboard?.writeText(nftAddress);
  }

  async function copyCapeTokenAddress() {
    if (!capeTokenAddress) return;
    await navigator.clipboard?.writeText(capeTokenAddress);
  }

  return (
    <main>
      <section
        style={{
          minHeight: "calc(100vh - var(--nav-height))",
          display: "flex",
          alignItems: "center",
          position: "relative",
          overflow: "hidden",
          paddingTop: "var(--nav-height)",
          background: `
            radial-gradient(ellipse 80% 60% at 50% -10%, rgba(0,255,65,0.16) 0%, transparent 70%),
            radial-gradient(ellipse 50% 40% at 85% 70%, rgba(192,76,253,0.12) 0%, transparent 60%),
            var(--color-void)
          `
        }}
      >
        <div style={{ position: "absolute", inset: 0, backgroundImage: `linear-gradient(var(--color-border) 1px, transparent 1px), linear-gradient(90deg, var(--color-border) 1px, transparent 1px)`, backgroundSize: "54px 54px", opacity: 0.22 }} />

        <div className="container" style={{ position: "relative", zIndex: 1 }}>
          <TrustBar />
          <div className="launch-link-strip" aria-label="Launch links" translate="no">
            <a href={xUrl} target="_blank" rel="noreferrer" className="launch-link-icon" aria-label="Open CODE APE on X" title="Open CODE APE on X" translate="no">𝕏</a>
            <a href={uniswapTradeUrl} target="_blank" rel="noreferrer" className="launch-link-icon launch-link-icon--trade" aria-label="Open Uniswap v4 trade page" title="Open Uniswap v4 trade page" translate="no">🦄</a>
            {nftAddress && nftExplorerUrl && (
              <span className="launch-link-pill launch-link-pill--contract" translate="no">
                <a href={nftExplorerUrl} target="_blank" rel="noreferrer">NFT <code>{shortAddress(nftAddress)}</code></a>
                <button type="button" className="launch-link-copy" onClick={copyNftAddress} aria-label="Copy NFT contract address" title="Copy NFT contract address">⧉</button>
              </span>
            )}
            {capeTokenAddress && capeTokenExplorerUrl && (
              <span className="launch-link-pill launch-link-pill--token" translate="no">
                <a href={capeTokenExplorerUrl} target="_blank" rel="noreferrer">CA <code>{shortAddress(capeTokenAddress)}</code></a>
                <button type="button" className="launch-link-copy" onClick={copyCapeTokenAddress} aria-label="Copy CAPE token contract address" title="Copy CAPE token contract address">⧉</button>
              </span>
            )}
          </div>
          <aside className="pre-hero" aria-label="Industry pivot">
            <span className="pre-hero__muted">// NFTs proved ownership.</span>
            <span className="pre-hero__strike">CODE APE proves the creation path.</span>
            <span className="pre-hero__arrow">→</span>
            <span className="pre-hero__accent">AI-agent handoff, wallet mint, holder fees, community LP.</span>
          </aside>
          <div className="hero-grid">
            <div>
              <div className="badge badge-prime hero-world-first" style={{ marginBottom: "var(--space-4)" }}>WORLD'S FIRST AI-SIGNED NFT</div>
              <h1 className="text-hero" style={{ marginBottom: "var(--space-5)" }}>
                Tell Codex first.<br />
                <span className="gradient-text">Mint a verifiable Ape.</span>
              </h1>
              <p className="text-xl text-muted" style={{ maxWidth: 720, marginBottom: "var(--space-6)", lineHeight: 1.65 }}>
                CODE APE turns AI-agent creation into a contract precondition. Codex is the recommended client today; the protocol checks the public Skill and wallet-bound handoff, not an AI vendor brand. Your wallet signs the final mint.
              </p>

              <div className="alert alert-info" style={{ marginBottom: "var(--space-6)", maxWidth: 760 }}>
                <span>🤖</span>
                <div>
                  <strong>Recommended: Codex</strong>
                  <pre className="prompt-spell" translate="no">{CODEX_PROMPT}</pre>
                  <p className="text-sm text-muted" style={{ marginTop: "var(--space-2)" }}>
                    Codex opens the compact Skill, requests a pre-pinned Ape mint pack, and returns a short mint link. Compatible agents can do the same if they can operate files, run the Skill, and call the authenticated backend flow.
                  </p>
                </div>
              </div>

              <div className="narrative-pill-row" aria-label="Protocol narrative">
                <span>Protocol-signed voucher</span>
                <span>Public Skill hash</span>
                <span>Wallet-bound handoff</span>
                <span>Vendor-neutral agent path</span>
              </div>

              <div style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap", marginBottom: "var(--space-5)" }}>
                <a href={SKILL_URL} className="btn btn-primary btn-lg" id="hero-open-skill-btn" target="_blank" rel="noreferrer">🚀 Start with Codex →</a>
                <button className="btn btn-secondary btn-lg" id="hero-copy-prompt-btn" onClick={copyPrompt}>📋 Copy the Summon Spell</button>
                <Link to="/agent-auth" className="btn btn-secondary btn-lg" id="hero-mint-console-btn">✍️ AI Agent Auth</Link>
              </div>

              <div className="hero-referral-callout">
                <span className="hero-referral-callout__badge">10%</span>
                <div>
                  <strong>Invite friends after you mint.</strong>
                  <p>Earn 10% of each successful referred mint, paid from the referral reward vault.</p>
                </div>
              </div>

              {nftAddress && nftExplorerUrl ? (
                <div className="hero-contract-card" aria-label="NFT contract address" translate="no">
                  <span>NFT contract</span>
                  <a href={nftExplorerUrl} target="_blank" rel="noreferrer"><code>{shortAddress(nftAddress)}</code></a>
                  <button type="button" onClick={copyNftAddress}>Copy</button>
                </div>
              ) : (
                <div className="hero-contract-card" aria-label="NFT contract status" translate="no">
                  <span>NFT contract</span>
                  <code>Pending deployment</code>
                </div>
              )}

              <div className="stat-grid" style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))", maxWidth: 760 }}>
                {STATS.map((s) => (
                  <div key={s.label} className="stat-card" style={{ padding: "var(--space-4)" }}>
                    <div className="stat-value gradient-text" style={{ fontSize: "var(--font-3xl)" }}>{s.value}</div>
                    <div className="stat-label">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>

<ApeCarousel />
          </div>
        </div>
      </section>

      <section className="section" style={{ background: "var(--color-void)", paddingTop: "var(--space-12)", paddingBottom: "var(--space-12)" }}>
        <div className="container">
          <div className="firsts-section">
          <div className="firsts-header">
              <div className="eyebrow">// origin, agent, hook, lp</div>
              <h2 className="firsts-title">A mint primitive for <span>AI-native NFTs.</span></h2>
            </div>
            <div className="firsts-grid-2x2">
              {FIRSTS.map((item) => <FirstCard key={item.index} item={item} />)}
            </div>
          </div>
        </div>
      </section>

      <section className="section" style={{ background: "var(--color-void)", paddingTop: "var(--space-12)", paddingBottom: "var(--space-12)" }}>
        <div className="container">
          <NftRightsStack showReferral />
        </div>
      </section>

      <section className="section" style={{ background: "var(--color-surface)", paddingTop: "var(--space-12)", paddingBottom: "var(--space-12)" }}>
        <div className="container">
          <TokenomicsDiagram />
        </div>
      </section>

      <section className="section" style={{ background: "var(--color-surface)", paddingTop: "var(--space-12)", paddingBottom: "var(--space-12)" }}>
        <div className="container">
          <div style={{ textAlign: "center", marginBottom: "var(--space-8)" }}>
            <div className="badge badge-prime" style={{ marginBottom: "var(--space-3)" }}>Two strategies</div>
            <h2 className="text-4xl">Hold or burn. Decide later.</h2>
          </div>
          <div className="grid-2">
            <ChoiceCard tone="green" badge="HOLD MODE" title="💧 Hold your Ape, earn WETH" items={HOLD_ITEMS} />
            <ChoiceCard tone="red" badge="BURN MODE" title="🔥 Burn your Ape, claim CAPE" items={BURN_ITEMS} />
          </div>
        </div>
      </section>

      <section className="section" style={{ background: "var(--color-void)", paddingTop: "var(--space-12)", paddingBottom: "var(--space-12)" }}>
        <div className="container">
          <EarningsSimulator />
        </div>
      </section>

      <section id="how-it-works" className="section" style={{ background: "var(--color-void)", paddingTop: "var(--space-12)", paddingBottom: "var(--space-12)" }}>
        <div className="container">
          <div style={{ textAlign: "center", marginBottom: "var(--space-8)" }}>
            <div className="badge badge-cyan" style={{ marginBottom: "var(--space-3)" }}>How it works</div>
            <h2 className="text-4xl">From agent auth to mint in four moves</h2>
          </div>
          <div className="how-grid">
            {HOW_IT_WORKS.map(([step, title, body]) => (
              <div key={step} className="card how-card">
                <div className="badge badge-prime" style={{ marginBottom: "var(--space-3)" }}>Step {step}</div>
                <h3 className="text-xl" style={{ marginBottom: "var(--space-2)" }}>{title}</h3>
                <p className="text-sm text-muted" style={{ lineHeight: 1.65 }}>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section" style={{ background: "var(--color-surface)", paddingTop: "var(--space-12)", paddingBottom: "var(--space-12)" }}>
        <div className="container">
          <div className="grid-2" style={{ alignItems: "start" }}>
            <div>
              <div className="badge badge-cyan" style={{ marginBottom: "var(--space-3)" }}>Plain-English proof</div>
              <h2 className="text-4xl" style={{ marginBottom: "var(--space-4)" }}>Why does this need an AI agent?</h2>
              <p className="text-muted" style={{ lineHeight: 1.7 }}>
                Because the Skill is part of the artifact. The image, metadata, reference pack, wallet, chain, contract, and signer are all tied into a handoff that your wallet can verify before minting.
              </p>
            </div>
            <div style={{ display: "grid", gap: "var(--space-3)" }}>
              {FAQ.map(([question, answer]) => (
                <details key={question} className="faq-item">
                  <summary>{question}</summary>
                  <p>{answer}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section" style={{ background: "var(--color-void)", paddingTop: "var(--space-12)", paddingBottom: "var(--space-12)" }}>
        <div className="container">
          <blockquote className="manifesto-callout">
            <p>Ownership was the first NFT primitive. Authorship is next.</p>
            <p>The recommended ritual starts with Codex. The protocol remains open to any compatible agent.</p>
            <p>Holder fees and community LP rights live in the NFT, not in a treasury story.</p>
          </blockquote>
          <div style={{ textAlign: "center", marginTop: "var(--space-5)" }}>
            <Link to="/manifesto" className="btn btn-secondary">Read the manifesto →</Link>
          </div>
        </div>
      </section>

      <section className="section" style={{ background: "var(--color-surface)", paddingTop: "var(--space-12)", paddingBottom: "var(--space-12)" }}>
        <div className="container">
          <div className="alert alert-warning" style={{ maxWidth: 1000, margin: "0 auto" }}>
            <span style={{ fontSize: "1.5rem" }}>⚠️</span>
            <div>
              <strong>Mint notes</strong>
              <ul className="risk-list">
                <li>Once minting is complete, no admin can pause it.</li>
                <li>You earn WETH only while swaps happen on the official pool. We do not guarantee swap volume.</li>
                <li>Sell-side swaps receive less WETH than no-hook quotes because the holder fee is collected.</li>
                <li>After launch, initial liquidity locks into UNCX V4 before trading opens. After the 90-day unlock, the community vault handles NFT-gated LP claims.</li>
                <li>Pending WETH belongs to whoever owns the NFT right now. If you list on a marketplace, claim first.</li>
              </ul>
              <Link to="/proof" style={{ color: "var(--color-gold)", marginTop: "var(--space-3)", display: "inline-block" }}>Full proof page →</Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

function FirstCard({ item }: { item: (typeof FIRSTS)[number] }) {
  return (
    <article className="card first-card first-card--large">
      <header className="first-card__head">
        <span className="first-num">{item.index}</span>
        <span className="first-card__pillar">{item.pillar}</span>
      </header>
      <h3 className="text-xl">{item.title}</h3>
      <p className="text-sm text-muted first-card__body">{item.body}</p>
      <p className="first-card__caveat">{item.caveat}</p>
    </article>
  );
}

function ChoiceCard({ tone, badge, title, items }: { tone: "green" | "red"; badge: string; title: string; items: string[] }) {
  return (
    <div className={`card choice-card choice-card--${tone}`} style={{ padding: "var(--space-6)" }}>
      <div className={`badge ${tone === "green" ? "badge-green" : "badge-red"}`} style={{ marginBottom: "var(--space-3)" }}>{badge}</div>
      <h3 className="text-2xl" style={{ marginBottom: "var(--space-4)" }}>{title}</h3>
      <ul style={{ display: "grid", gap: "var(--space-3)", color: "var(--text-secondary)", lineHeight: 1.65 }}>
        {items.map((item) => <li key={item}>{item}</li>)}
      </ul>
    </div>
  );
}
